/* Studio Sash — motion layer
   reveals · word splits · parallax · hero arch-on-scroll · Palace Edit arcade
   smooth wheel scroll · cursor follower · magnetic buttons · scroll-reactive marquee · mobile sticky add-to-bag */
(() => {
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = mqReduce.matches;
  const design = document.documentElement.classList.contains('shopify-design-mode') || (window.Shopify && window.Shopify.designMode);
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  /* ---------- scroll bus: one rAF loop for every scroll effect ---------- */
  const scrollFns = [];
  let scrollQueued = false;
  const runScroll = () => { scrollQueued = false; const y = window.scrollY; scrollFns.forEach((fn) => fn(y)); };
  const queueScroll = () => { if (!scrollQueued) { scrollQueued = true; requestAnimationFrame(runScroll); } };
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll);

  /* ---------- word splitting ---------- */
  function splitWords(el) {
    if (el.dataset.ssSplit === 'done') return;
    const text = el.textContent.trim().replace(/\s+/g, ' ');
    el.setAttribute('aria-label', text);
    el.textContent = '';
    text.split(' ').forEach((word, i) => {
      const outer = document.createElement('span');
      outer.className = 'ss-w';
      outer.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span');
      inner.textContent = word;
      inner.style.setProperty('--ss-wi', i);
      outer.appendChild(inner);
      el.appendChild(outer);
      el.appendChild(document.createTextNode(' '));
    });
    el.classList.add('ss-words');
    el.dataset.ssSplit = 'done';
  }

  /* ---------- reveal on view ---------- */
  let io;
  const REVEAL = '.ss-reveal:not(.is-in), .ss-mask:not(.is-in), .ss-words:not(.is-in), .ss-kolam:not(.is-in), .ss-rise:not(.is-in)';
  function observe(root = document) {
    root.querySelectorAll('[data-ss-split]').forEach(splitWords);
    const targets = root.querySelectorAll(REVEAL);
    if (reduce || design || !('IntersectionObserver' in window)) { targets.forEach((t) => t.classList.add('is-in')); return; }
    io = io || new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        if (e.target._ssMasks) e.target._ssMasks.forEach((m) => m.classList.add('is-in'));
        else e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    targets.forEach((t) => {
      if (t.classList.contains('ss-mask')) {           /* clip-path hides a mask from the observer: watch its parent */
        const trigger = t.parentElement;
        (trigger._ssMasks = trigger._ssMasks || []).push(t);
        io.observe(trigger);
      } else io.observe(t);
    });
  }

  /* ---------- parallax: data-ss-parallax="0.15" ---------- */
  function parallax() {
    if (reduce) return;
    const els = Array.from(document.querySelectorAll('[data-ss-parallax]'));
    if (!els.length) return;
    scrollFns.push(() => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const speed = parseFloat(el.dataset.ssParallax) || 0.15;
        el.style.transform = `translate3d(0, ${((r.top + r.height / 2 - vh / 2) * -speed).toFixed(1)}px, 0)`;
      });
    });
  }

  /* ---------- hero: settles into a palace arch as you scroll ---------- */
  function heroArch() {
    const hero = document.querySelector('[data-ss-hero]');
    if (!hero || reduce) return;
    scrollFns.push((y) => {
      const h = hero.offsetHeight || 1;
      const top = hero.getBoundingClientRect().top + y;
      hero.style.setProperty('--p', clamp((y - top) / (h * 0.9), 0, 1).toFixed(3));
    });
  }

  /* ---------- Palace Edit: pinned horizontal arcade (desktop) ---------- */
  function arcades() {
    document.querySelectorAll('[data-ss-arcade]').forEach((sec) => {
      const pin = sec.querySelector('.ss-arcade__pin');
      const track = sec.querySelector('.ss-arcade__track');
      if (!pin || !track) return;
      const mq = window.matchMedia('(min-width: 990px)');
      let dist = 0;
      const layout = () => {
        if (!mq.matches || reduce || design) {
          sec.classList.remove('is-pinned'); sec.style.height = ''; track.style.transform = ''; return;
        }
        sec.classList.add('is-pinned');
        dist = Math.max(0, track.scrollWidth - window.innerWidth);
        sec.style.height = `${window.innerHeight + dist}px`;
        queueScroll();
      };
      scrollFns.push((y) => {
        if (!sec.classList.contains('is-pinned')) return;
        const top = sec.getBoundingClientRect().top + y;
        const p = clamp((y - top) / Math.max(1, sec.offsetHeight - window.innerHeight), 0, 1);
        track.style.transform = `translate3d(${(-p * dist).toFixed(1)}px, 0, 0)`;
        sec.style.setProperty('--ss-arcade-p', p.toFixed(3));
      });
      layout();
      mq.addEventListener('change', layout);
      window.addEventListener('resize', layout);
      track.querySelectorAll('img').forEach((img) => { if (!img.complete) img.addEventListener('load', layout, { once: true }); });
    });
  }

  /* ---------- smooth wheel scrolling (desktop mouse only) ---------- */
  function smoothScroll() {
    if (reduce || design || !finePointer) return;
    let target = window.scrollY, current = window.scrollY, running = false;
    const locked = () => {
      const b = document.body, h = document.documentElement;
      return getComputedStyle(b).overflow === 'hidden' || getComputedStyle(h).overflow === 'hidden' ||
        b.classList.contains('overflow-hidden') || b.classList.contains('overflow-hidden-desktop') || b.classList.contains('overflow-hidden-tablet') || b.classList.contains('overflow-hidden-mobile');
    };
    const scrollableParent = (el) => {
      for (let n = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
        if (n.nodeType !== 1) continue;
        const s = getComputedStyle(n);
        if (/(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight + 1) return n;
        if (n.tagName === 'IFRAME' || n.isContentEditable) return n;
      }
      return null;
    };
    const step = () => {
      current += (target - current) * 0.11;
      if (Math.abs(target - current) < 0.5) { current = target; running = false; }
      window.scrollTo({ top: current, left: 0, behavior: 'instant' });   /* bypass CSS scroll-behavior */
      if (running) requestAnimationFrame(step);
    };
    window.addEventListener('wheel', (e) => {
      if (e.ctrlKey || e.defaultPrevented || locked() || scrollableParent(e.target)) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      if (!running) { current = window.scrollY; target = current; }
      const delta = e.deltaMode === 1 ? e.deltaY * 32 : e.deltaMode === 2 ? e.deltaY * window.innerHeight : e.deltaY;
      target = clamp(target + delta, 0, document.documentElement.scrollHeight - window.innerHeight);
      if (!running) { running = true; requestAnimationFrame(step); }
    }, { passive: false });
    window.addEventListener('scroll', () => { if (!running) { target = current = window.scrollY; } }, { passive: true });
  }

  /* ---------- cursor follower + "View" state ---------- */
  function cursor() {
    if (reduce || design || !finePointer) return;
    const c = document.createElement('div');
    c.className = 'ss-cursor is-hidden';
    c.innerHTML = '<span class="ss-cursor__ring"><span class="ss-cursor__label">View</span></span><span class="ss-cursor__dot"></span>';
    document.body.appendChild(c);
    const ring = c.querySelector('.ss-cursor__ring'), dot = c.querySelector('.ss-cursor__dot'), label = c.querySelector('.ss-cursor__label');
    let mx = -100, my = -100, rx = -100, ry = -100;
    window.addEventListener('mousemove', (e) => {
      mx = e.clientX; my = e.clientY; c.classList.remove('is-hidden');
      dot.style.transform = `translate3d(${mx}px, ${my}px, 0)`;
      const t = e.target.closest ? e.target : null;
      const view = t && t.closest('[data-ss-cursor], .card-wrapper, .ss-cats__link');
      const link = t && t.closest('a, button, summary, [role="button"], label, select');
      c.classList.toggle('is-view', !!view);
      c.classList.toggle('is-link', !view && !!link);
      if (view) label.textContent = (view.dataset && view.dataset.ssCursor) || 'View';
    }, { passive: true });
    document.addEventListener('mouseleave', () => c.classList.add('is-hidden'));
    window.addEventListener('blur', () => c.classList.add('is-hidden'));
    const loop = () => {
      rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18;
      ring.style.transform = `translate3d(${rx.toFixed(1)}px, ${ry.toFixed(1)}px, 0)`;
      requestAnimationFrame(loop);
    };
    loop();
  }

  /* ---------- magnetic buttons ---------- */
  function magnetic() {
    if (reduce || design || !finePointer) return;
    document.querySelectorAll('.button:not(.product-form__submit):not(.shopify-payment-button__button), .ss-hero__link, .ss-arcade__end').forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) / r.width, y = (e.clientY - r.top - r.height / 2) / r.height;
        el.style.transform = `translate(${(x * 10).toFixed(1)}px, ${(y * 8).toFixed(1)}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    });
  }

  /* ---------- marquee speeds up with scroll velocity ---------- */
  function marqueeVelocity() {
    if (reduce) return;
    const anims = [];
    document.querySelectorAll('.ss-marquee__track').forEach((t) => { const a = t.getAnimations ? t.getAnimations()[0] : null; if (a) anims.push(a); });
    if (!anims.length) return;
    let last = window.scrollY, boost = 0;
    scrollFns.push((y) => { boost = Math.min(4, boost + Math.abs(y - last) / 60); last = y; });
    const decay = () => { boost *= 0.92; anims.forEach((a) => { a.playbackRate = 1 + boost; }); requestAnimationFrame(decay); };
    decay();
  }

  /* ---------- mobile sticky add-to-bag on product pages ---------- */
  function stickyAtc() {
    const submit = document.querySelector('.product__info-container .product-form__submit');
    if (!submit || document.querySelector('.ss-sticky-atc')) return;
    const title = document.querySelector('.product__title h1, .product__title h2');
    const bar = document.createElement('div');
    bar.className = 'ss-sticky-atc';
    bar.innerHTML = '<div class="ss-sticky-atc__info"><div class="ss-sticky-atc__title"></div><div class="ss-sticky-atc__price"></div></div><button type="button" class="button button--primary">Add to bag</button>';
    bar.querySelector('.ss-sticky-atc__title').textContent = title ? title.textContent.trim() : '';
    const priceEl = bar.querySelector('.ss-sticky-atc__price');
    const btn = bar.querySelector('button');
    const sync = () => {
      const p = document.querySelector('.product__info-container .price__container .price-item--last, .product__info-container .price-item--regular');
      priceEl.textContent = p ? p.textContent.trim() : '';
      btn.disabled = submit.disabled;
      btn.textContent = submit.disabled ? (submit.textContent.trim() || 'Sold out') : 'Add to bag';
    };
    sync();
    new MutationObserver(sync).observe(submit, { attributes: true, childList: true, subtree: true });
    btn.addEventListener('click', () => { submit.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); submit.click(); });
    document.body.appendChild(bar);
    new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0)).observe(submit);
  }

  function init() {
    observe(); parallax(); heroArch(); arcades(); smoothScroll(); cursor(); magnetic(); marqueeVelocity(); stickyAtc();
    queueScroll();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', (e) => { observe(e.target); queueScroll(); });
})();
